import { useEffect, useRef, type ReactNode } from 'react';
import { Camera, FileUp, Hash, Mic, Pencil, AtSign } from 'lucide-react';
import type { QuickCaptureSuggestEntity } from '@leanopx/shared';
import { ConfidenceMeter, EntityName, SlideOver } from '@leanopx/design-system';
import { cn } from '@leanopx/design-system';
import { t } from '@/lib/i18n';
import { useArmedConfirm } from '@/lib/use-armed-confirm';
import type { QuickCaptureViewProps } from './quick-capture-provider';

/**
 * QuickCapture — right-anchored 440px SlideOver that lets the operator
 * drop a thought from anywhere in the portal. The platform stays visible
 * behind it (scrim="transparent") so the user can keep reading the dossier
 * while capturing the related note.
 *
 * Composition:
 *   - Built on `<SlideOver emphasis="accent" scrim="transparent">` from
 *     `@leanopx/design-system/overlay/slide-over`. The accent emphasis
 *     swaps the 1px hairline for a 1.5px gold inline-start edge — the
 *     load-bearing visual cue that says "agent / capture surface."
 *   - Header (sticky) carries an accent eyebrow + optional "thinking
 *     about <entity>" sublabel + kbd-hint + close X. The SlideOver
 *     primitive owns the close button; the kbd-hint sits inside the
 *     `sub` slot.
 *   - Body (scrollable): textarea → capture-tool toolbar → "Attach to"
 *     entity pill row → "Agent suggests" callout.
 *   - Footer (sticky): "Private to you" affordance + Discard / Save&link
 *     CTAs.
 *
 * Reference design: `/tmp/leanopx-design--NVURoc16TJeOLvm23XVag/leanopx/
 * project/assets/capture.jsx` lines 69–256.
 *
 * Open/close + draft state live in `QuickCaptureProvider` — this file is
 * a dumb-component surface.
 */
export function QuickCapture(props: QuickCaptureViewProps) {
  const {
    open,
    context,
    body,
    onBodyChange,
    targets,
    onRemoveTarget,
    onClose,
    onDiscard,
    onSave,
    saving,
    saveDisabled,
    suggestions,
    suggestionsLoading,
    onAddAllSuggestions,
    onAddSuggestion,
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea as the user types — the design calls for a
  // minimum 110px height that expands without showing a scrollbar until
  // the panel itself overflows.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.blockSize = 'auto';
    el.style.blockSize = `${Math.max(el.scrollHeight, 110)}px`;
  }, [body]);

  // Focus the textarea once the panel opens so the user lands in the
  // capture field without a click. The 100ms defer waits for Radix to
  // mount + animate; focusing earlier loses to the focus-trap initial
  // focus.
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
    return () => window.clearTimeout(handle);
  }, [open]);

  // Discard with a soft two-step confirm if the user has unsaved
  // content — single click on an empty draft is a no-op close.
  const hasDraft = body.trim().length > 0 || targets.length > 0;
  const discardConfirm = useArmedConfirm({
    onConfirm: () => {
      onDiscard();
    },
    holdMs: 3000,
  });

  const handleDiscardClick = () => {
    if (!hasDraft) {
      onDiscard();
      return;
    }
    discardConfirm.trigger();
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      emphasis="accent"
      scrim="transparent"
      eyebrow={
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block size-1.5 rounded-full bg-[var(--color-accent)] shadow-[0_0_8px_var(--color-accent)]"
          />
          {t('quickCapture.eyebrow')}
        </span>
      }
      title={t('quickCapture.title')}
      sub={
        <span className="flex items-center justify-between gap-2">
          <span className="truncate">
            {context ? (
              <>
                <span>{t('quickCapture.contextPrefix')}</span>
                <strong className="font-semibold text-[var(--color-ink)]">{context.name}</strong>
              </>
            ) : null}
          </span>
          <kbd
            aria-hidden
            className="inline-flex shrink-0 items-center rounded-1 border border-[var(--color-line-2)] px-1.5 py-px font-mono text-2xs uppercase tracking-[0.1em] text-[var(--color-ink-muted)]"
          >
            {t('quickCapture.kbdHint')}
          </kbd>
        </span>
      }
      closeLabel={t('quickCapture.closeLabel')}
      primaryCta={{
        label: saving ? t('quickCapture.saving') : t('quickCapture.save'),
        onClick: onSave,
        disabled: saveDisabled,
      }}
      secondaryCta={{
        label: discardConfirm.armed ? t('quickCapture.discardArmed') : t('quickCapture.discard'),
        onClick: handleDiscardClick,
      }}
    >
      <div className="flex flex-col gap-4">
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(event) => onBodyChange(event.target.value)}
          placeholder={t('quickCapture.placeholder')}
          rows={5}
          className={cn(
            'w-full resize-none bg-transparent text-[15px] leading-relaxed text-[var(--color-ink)]',
            'placeholder:text-[var(--color-ink-muted)]',
            'border-0 outline-none focus:outline-none focus-visible:outline-none',
            'font-sans',
          )}
        />

        <CaptureToolbar />

        <AttachToSection context={context} targets={targets} onRemoveTarget={onRemoveTarget} />

        <AgentSuggestsCallout
          suggestions={suggestions}
          loading={suggestionsLoading}
          targets={targets}
          onAddAllSuggestions={onAddAllSuggestions}
          onAddSuggestion={onAddSuggestion}
        />

        <FootnotePrivate />
      </div>
    </SlideOver>
  );
}

/**
 * Capture-tool toolbar — six mono-icon buttons matching the design's
 * `capture.jsx` ordering. The voice button is visually "active" (accent
 * tint) to match the reference, but for v1 the buttons are inert; the
 * upload plumbing reuses the dossier-notes editor in a follow-up.
 */
function CaptureToolbar() {
  const tools: Array<{
    key: string;
    icon: ReactNode;
    label: string;
    tone: 'accent' | 'steel' | 'sage' | 'rose' | 'muted';
    active?: boolean;
  }> = [
    {
      key: 'voice',
      icon: <Mic aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.voice'),
      tone: 'accent',
      active: true,
    },
    {
      key: 'photo',
      icon: <Camera aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.photo'),
      tone: 'steel',
    },
    {
      key: 'file',
      icon: <FileUp aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.file'),
      tone: 'sage',
    },
    {
      key: 'sketch',
      icon: <Pencil aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.sketch'),
      tone: 'rose',
    },
    {
      key: 'mention',
      icon: <AtSign aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.mention'),
      tone: 'muted',
    },
    {
      key: 'tag',
      icon: <Hash aria-hidden className="size-3.5" />,
      label: t('quickCapture.tools.tag'),
      tone: 'muted',
    },
  ];

  return (
    <div>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1 rounded-2 border border-[var(--color-line)] bg-[var(--color-surface-2)] p-1',
        )}
      >
        {tools.map((tool) => (
          <button
            key={tool.key}
            type="button"
            disabled
            aria-disabled
            title={t('quickCapture.toolsUnavailable')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-1 px-2 py-1 text-xs font-medium',
              'cursor-not-allowed opacity-80',
              tool.active
                ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                : 'bg-transparent text-[var(--color-ink-secondary)]',
            )}
          >
            <span className={cn('font-mono', toneClass(tool.tone))}>{tool.icon}</span>
            {tool.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-2xs text-[var(--color-ink-muted)]">
        {t('quickCapture.toolsUnavailable')}
      </p>
    </div>
  );
}

function toneClass(tone: 'accent' | 'steel' | 'sage' | 'rose' | 'muted'): string {
  switch (tone) {
    case 'accent':
      return 'text-[var(--color-accent)]';
    case 'steel':
      return 'text-[var(--color-steel)]';
    case 'sage':
      return 'text-[var(--color-sage)]';
    case 'rose':
      return 'text-[var(--color-rose)]';
    case 'muted':
      return 'text-[var(--color-ink-muted)]';
  }
}

/**
 * "Attach to" entity pill row. Renders the context entity (if any)
 * filled with its role color + the placeholder "+ Add" pill. The picker
 * itself opens in a follow-up — the prop callback is wired through so
 * the next agent only needs to mount the picker.
 */
function AttachToSection({
  context,
  targets,
  onRemoveTarget,
}: {
  context: QuickCaptureViewProps['context'];
  targets: QuickCaptureViewProps['targets'];
  onRemoveTarget: QuickCaptureViewProps['onRemoveTarget'];
}) {
  return (
    <div>
      <div className="font-mono text-2xs uppercase tracking-[0.16em] text-[var(--color-ink-muted)]">
        {t('quickCapture.attachToHeading')}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {targets.map((target) => {
          const isContext = context?.key === target.key;
          return (
            <button
              key={target.key}
              type="button"
              onClick={() => onRemoveTarget(target.key)}
              className={cn(
                'group inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-2xs',
                'border border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
                'transition-opacity hover:opacity-80',
              )}
            >
              <span
                aria-hidden
                className="inline-block size-1 rounded-full bg-[var(--color-accent)]"
              />
              <span className="text-[var(--color-ink)]">{target.name}</span>
              <span className="text-[var(--color-ink-muted)]">· {target.kind}</span>
              {!isContext && (
                <span aria-hidden className="ms-1 text-[var(--color-ink-muted)]">
                  ✕
                </span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          disabled
          aria-disabled
          title={t('quickCapture.toolsUnavailable')}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-2xs',
            'border border-dashed border-[var(--color-line-2)] bg-[var(--color-surface-2)] text-[var(--color-ink-secondary)]',
            'cursor-not-allowed opacity-80',
          )}
        >
          {t('quickCapture.attachAdd')}
        </button>
      </div>
    </div>
  );
}

/**
 * "Agent suggests" callout — accent-soft background + accent border.
 * Populated from the debounced `/api/orgs/:o/quick-capture/suggest` call
 * fired by the provider; each row carries an `<EntityName>` + 1-line
 * reason + compact `<ConfidenceMeter>` (per CLAUDE.md's "every AI-
 * surfaced claim carries confidence" rule). The "+ Add all N" CTA
 * appends every suggestion to the targets[] list at once — the user
 * can deselect individually after.
 *
 * Visibility:
 *   - hidden entirely when there's nothing to show and nothing in flight
 *     (the draft is shorter than the suggest threshold)
 *   - shows a quiet "thinking…" line while loading and no prior results
 *     are still on screen
 *   - shows the empty-state body copy when the model returned 0 picks
 *     for a substantive draft (lets the CEO know the agent looked)
 */
function AgentSuggestsCallout({
  suggestions,
  loading,
  targets,
  onAddAllSuggestions,
  onAddSuggestion,
}: {
  suggestions: QuickCaptureSuggestEntity[];
  loading: boolean;
  targets: QuickCaptureViewProps['targets'];
  onAddAllSuggestions: () => void;
  onAddSuggestion: (suggestion: QuickCaptureSuggestEntity) => void;
}) {
  // Nothing to show, nothing in flight — keep the panel quiet so the
  // callout doesn't take vertical space before the user has typed.
  if (!loading && suggestions.length === 0) return null;

  const targetKeys = new Set(targets.map((t) => `${t.kind}::${t.key}`));
  const unaddedCount = suggestions.filter((s) => !targetKeys.has(`${s.kind}::${s.key}`)).length;

  return (
    <div
      className={cn(
        'rounded-2 border border-[var(--color-accent)] p-3',
        'bg-[var(--color-accent-soft)]',
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 32 32" fill="none" aria-hidden>
            <circle cx="16" cy="16" r="14" stroke="var(--color-ink-faint)" strokeWidth="1.5" />
            <circle cx="16" cy="16" r="8" stroke="var(--color-ink-muted)" strokeWidth="1.5" />
            <circle cx="16" cy="16" r="3" fill="var(--color-accent)" />
          </svg>
          <span className="font-mono text-2xs uppercase tracking-[0.16em] text-[var(--color-accent)]">
            {t('quickCapture.agentSuggestsEyebrow')}
          </span>
          {loading && (
            <span className="font-mono text-2xs text-[var(--color-ink-muted)]">
              {t('quickCapture.agentSuggestsThinking')}
            </span>
          )}
        </div>
        {unaddedCount > 0 && (
          <button
            type="button"
            onClick={onAddAllSuggestions}
            className={cn(
              'inline-flex items-center rounded-1 border border-[var(--color-accent)] px-2 py-0.5',
              'font-mono text-2xs text-[var(--color-accent)]',
              'transition-opacity hover:opacity-80',
            )}
          >
            {t('quickCapture.agentSuggestsAddAll')} {unaddedCount}
          </button>
        )}
      </div>

      {suggestions.length === 0 ? (
        <p className="text-xs leading-relaxed text-[var(--color-ink-muted)]">
          {t('quickCapture.agentSuggestsEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {suggestions.map((s) => {
            const alreadyAdded = targetKeys.has(`${s.kind}::${s.key}`);
            return (
              <li key={`${s.kind}::${s.key}`} className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <EntityName name={s.label} className="text-xs text-[var(--color-ink)]" />
                    <span className="font-mono text-2xs text-[var(--color-ink-muted)]">
                      · {s.kind}
                    </span>
                    <ConfidenceMeter compact value={s.confidence} />
                  </div>
                  {s.reason && (
                    <p className="mt-0.5 text-2xs leading-relaxed text-[var(--color-ink-secondary)]">
                      {s.reason}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onAddSuggestion(s)}
                  disabled={alreadyAdded}
                  aria-disabled={alreadyAdded}
                  className={cn(
                    'shrink-0 rounded-1 border px-1.5 py-0.5 font-mono text-2xs',
                    alreadyAdded
                      ? 'cursor-not-allowed border-[var(--color-line)] text-[var(--color-ink-muted)] opacity-70'
                      : 'border-[var(--color-accent)] text-[var(--color-accent)] hover:opacity-80',
                  )}
                >
                  {alreadyAdded
                    ? t('quickCapture.agentSuggestsAdded')
                    : t('quickCapture.agentSuggestsAddOne')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * "Private to you" footnote — sits in the scrollable body just above
 * the sticky footer because the SlideOver primitive's footer slot is
 * reserved for CTAs. The footer itself still carries the Discard /
 * Save&link buttons.
 */
function FootnotePrivate() {
  return (
    <div className="flex items-center gap-1.5 text-2xs text-[var(--color-ink-muted)]">
      <span aria-hidden className="inline-block size-1.5 rounded-full bg-[var(--color-sage)]" />
      {t('quickCapture.privateLabel')}
    </div>
  );
}
